import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Inbox, CheckCircle2, RotateCcw, Trash2, Mail, Send, User as UserIcon } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const FILTERS = ["open", "resolved", "all"];

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

function previewLabel(role) {
  if (role === "user") return "Customer: ";
  if (role === "admin") return "Team: ";
  return "Bot: ";
}

function isWaiting(c) {
  return c.status === "open" && c.last_role === "user";
}

export const AdminInbox = ({ onChanged }) => {
  const [filter, setFilter] = useState("open");
  const [chats, setChats] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [replying, setReplying] = useState(false);

  const load = useCallback(() => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/admin/support/chats${q}`)
      .then(({ data }) => {
        setChats(data);
        onChanged?.();
      })
      .catch(() => {});
  }, [filter, onChanged]);

  useEffect(() => { load(); }, [load]);

  const openChat = async (sessionId) => {
    try {
      const { data } = await api.get(`/admin/support/chats/${sessionId}`);
      setActive(data);
      setReply("");
      setChats((list) => list.map((x) => (x.session_id === sessionId ? { ...x, admin_unread: false } : x)));
    } catch {
      toast.error("Failed to load conversation");
    }
  };

  const setStatus = async (sessionId, status) => {
    try {
      await api.put(`/admin/support/chats/${sessionId}/status`, { status });
      toast.success(status === "resolved" ? "Marked resolved" : "Reopened");
      setActive((a) => (a && a.session_id === sessionId ? { ...a, status } : a));
      load();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const remove = async (sessionId) => {
    if (!window.confirm("Delete this conversation permanently?")) return;
    try {
      await api.delete(`/admin/support/chats/${sessionId}`);
      toast.success("Conversation deleted");
      setActive(null);
      load();
    } catch {
      toast.error("Failed to delete conversation");
    }
  };

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || replying || !active) return;
    setReplying(true);
    try {
      const { data } = await api.post(`/admin/support/chats/${active.session_id}/reply`, { text });
      setActive((a) => ({ ...a, messages: [...(a.messages || []), data], status: "open" }));
      setReply("");
      load();
    } catch {
      toast.error("Failed to send reply");
    } finally {
      setReplying(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`inbox-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`text-xs tracking-[0.15em] uppercase font-semibold px-4 py-2 rounded-none border transition-colors ${
              filter === f ? "bg-[#8C7B6B] text-white border-[#8C7B6B]" : "border-[#DDD7CF] text-[#6E675F] hover:border-[#8C7B6B]"
            }`}
          >
            {f}
          </button>
        ))}
        <p className="text-sm text-[#6E675F] ml-auto" data-testid="inbox-count">
          <strong className="text-[#1A1A1A]">{chats.length}</strong> conversation{chats.length === 1 ? "" : "s"}
        </p>
      </div>

      {chats.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#DDD7CF] rounded-lg" data-testid="inbox-empty">
          <Inbox className="w-10 h-10 text-[#8C7B6B] mx-auto mb-4" />
          <p className="text-[#6E675F]">No {filter === "all" ? "" : filter + " "}conversations. Chat messages from the site widget land here.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="inbox-list">
          {[...chats].sort((a, b) => (b.admin_unread ? 1 : 0) - (a.admin_unread ? 1 : 0) || isWaiting(b) - isWaiting(a)).map((c) => (
            <button
              key={c.session_id}
              data-testid={`inbox-chat-${c.session_id}`}
              onClick={() => openChat(c.session_id)}
              className={`w-full text-left border rounded-lg p-4 transition-colors ${
                isWaiting(c)
                  ? "border-amber-400 border-l-4 bg-amber-50/50 hover:border-amber-500"
                  : "border-[#DDD7CF] bg-white hover:border-[#8C7B6B]"
              }`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                {c.admin_unread && (
                  <span
                    data-testid={`inbox-unread-dot-${c.session_id}`}
                    title="Unread customer messages"
                    className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0 animate-pulse"
                  />
                )}
                <span className={`inline-flex items-center gap-1.5 text-sm text-[#1A1A1A] ${c.admin_unread ? "font-bold" : "font-medium"}`}>
                  {c.email ? <Mail className="w-3.5 h-3.5 text-[#8C7B6B]" /> : <UserIcon className="w-3.5 h-3.5 text-[#8C7B6B]" />}
                  {c.name || c.email || "Anonymous visitor"}
                </span>
                {c.email && c.name && <span className="text-xs text-[#6E675F]">{c.email}</span>}
                <span className={`text-[10px] tracking-[0.12em] uppercase font-bold px-2 py-0.5 rounded-none border ${
                  c.status === "resolved" ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-amber-50 text-amber-800 border-amber-300"
                }`}>
                  {c.status}
                </span>
                {isWaiting(c) && (
                  <span
                    data-testid={`inbox-awaiting-chip-${c.session_id}`}
                    className="text-[10px] tracking-[0.12em] uppercase font-bold px-2 py-0.5 rounded-none bg-red-50 text-red-700 border border-red-300"
                  >
                    awaiting reply
                  </span>
                )}
                <span className="text-xs text-[#6E675F] ml-auto">{c.message_count} msg · {when(c.updated_at)}</span>
              </div>
              <p className="text-sm text-[#6E675F] mt-2 truncate">
                <span className="font-semibold text-[#1A1A1A]">{previewLabel(c.last_role)}</span>
                {c.last_message}
              </p>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="inbox-chat-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display uppercase tracking-wide">
                  {active.name || active.email || "Anonymous visitor"}
                </DialogTitle>
                <DialogDescription>
                  {active.email ? `Follow up: ${active.email}` : "No email left"} · started {when(active.created_at)}
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto space-y-3 py-2 pr-1" data-testid="inbox-transcript">
                {(active.messages || []).map((m, i) => (
                  <div key={`${i}-${m.at}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line rounded-2xl ${
                      m.role === "user"
                        ? "bg-[#8C7B6B] text-white rounded-br-md"
                        : m.role === "admin"
                          ? "bg-[#1A1A1A] text-white rounded-bl-md"
                          : "bg-[#EFEBE6] text-[#1A1A1A] rounded-bl-md"
                    }`}>
                      {m.role === "admin" && (
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#C9BBA8] mb-1">
                          {m.admin_name || "Team"}
                        </p>
                      )}
                      {m.text}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-3 border-t border-[#DDD7CF]">
                <Input
                  data-testid="inbox-reply-input"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendReply()}
                  placeholder="Reply as the team — the customer sees it in their chat…"
                  className="flex-1"
                />
                <Button
                  data-testid="inbox-reply-send"
                  onClick={sendReply}
                  disabled={replying || !reply.trim()}
                  className="bg-[#8C7B6B] text-white hover:bg-[#75665A] shrink-0"
                  aria-label="Send reply"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex gap-2 pt-2">
                {active.status === "open" ? (
                  <Button data-testid="inbox-resolve-button" onClick={() => setStatus(active.session_id, "resolved")} className="flex-1 bg-[#2D6A4F] hover:bg-[#245741]">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark Resolved
                  </Button>
                ) : (
                  <Button data-testid="inbox-reopen-button" variant="outline" onClick={() => setStatus(active.session_id, "open")} className="flex-1">
                    <RotateCcw className="w-4 h-4 mr-1.5" /> Reopen
                  </Button>
                )}
                <Button data-testid="inbox-delete-button" variant="outline" onClick={() => remove(active.session_id)} className="text-red-700 border-red-200 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
